import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Camera, ArrowLeft } from "lucide-react";
import SEO from "../components/SEO";

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Privacy Policy | 360Lumina"
        description="Read 360Lumina's Privacy Policy. Learn how we collect, use, and protect your personal information on our photography platform."
        canonical="https://www.360lumina.com/privacy"
      />
      {/* Header */}
      <header className="border-b border-border py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Camera className="w-6 h-6 text-primary" />
            <span className="text-xl font-serif">360Lumina</span>
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-serif mb-2">Privacy Policy</h1>
        <p className="text-muted-foreground mb-8">Effective Date: January 1, 2026</p>

        <div className="prose prose-invert max-w-none space-y-8">
          <p>
            This Privacy Policy describes how 360Lumina LLC ("360Lumina," "we," "us," or "our") collects, uses, discloses, 
            and safeguards personal information in connection with the 360Lumina platform, website, and related services 
            (collectively, the "Platform"). By accessing or using the Platform, you consent to the collection and use of 
            information as described in this Privacy Policy.
          </p>

          <section>
            <h2 className="text-2xl font-serif mb-4">1. Information We Collect</h2>

            <h3 className="text-xl font-medium mt-6 mb-3">1.1 Information You Provide</h3>
            <p>We may collect personal information you voluntarily provide, including:</p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Name</li>
              <li>Email address</li>
              <li>Account credentials</li>
              <li>Business information (for photographers)</li>
              <li>Booking details and communications</li>
              <li>Uploaded content, including photographs and metadata</li>
            </ul>

            <h3 className="text-xl font-medium mt-6 mb-3">1.2 Information Collected Automatically</h3>
            <p>When you use the Platform, we may automatically collect:</p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>IP address</li>
              <li>Device and browser information</li>
              <li>Log files and timestamps</li>
              <li>Usage data and interactions</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>

            <h3 className="text-xl font-medium mt-6 mb-3">1.3 Payment Information</h3>
            <p>
              Payments are processed by third-party payment processors such as Stripe, Inc. 360Lumina does not store 
              credit card or banking information. Payment data is handled directly by Stripe in accordance with its 
              privacy practices.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">2. How We Use Information</h2>
            <p>We use personal information to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Provide and operate the Platform</li>
              <li>Facilitate bookings and transactions</li>
              <li>Deliver emails and notifications</li>
              <li>Host and deliver digital content</li>
              <li>Secure and maintain Platform performance</li>
              <li>Prevent fraud and abuse</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">3. How We Share Information</h2>
            <p>We may share information with trusted third parties solely to operate the Platform.</p>

            <h3 className="text-xl font-medium mt-6 mb-3">3.1 Service Providers</h3>
            <p>We use the following categories of service providers:</p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li><strong>Payment Processing:</strong> Stripe, Inc.</li>
              <li><strong>Email Delivery:</strong> SendGrid</li>
              <li><strong>Content Delivery, Storage, and Security:</strong> Cloudflare (including Cloudflare R2 object storage)</li>
              <li><strong>Authentication & Productivity:</strong> Google Workspace</li>
            </ul>
            <p className="mt-4">
              These providers process data on our behalf under contractual obligations consistent with this Privacy Policy.
            </p>

            <h3 className="text-xl font-medium mt-6 mb-3">3.2 Legal Requirements</h3>
            <p>
              We may disclose information if required by law, subpoena, court order, or to protect our rights, users, or Platform.
            </p>

            <h3 className="text-xl font-medium mt-6 mb-3">3.3 Business Transfers</h3>
            <p>
              If 360Lumina is involved in a merger, acquisition, or asset sale, personal data may be transferred as part 
              of that transaction.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">4. Cloudflare R2 and Content Storage</h2>
            <p>
              Photographs and other digital content uploaded to the Platform are stored using Cloudflare R2, an object 
              storage service designed for secure content delivery.
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Content may be cached or distributed via Cloudflare's global network to improve performance</li>
              <li>Cloudflare acts as a data processor on behalf of 360Lumina</li>
              <li>Content access is governed by Platform permissions and security controls</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">5. Email Communications (SendGrid)</h2>
            <p>360Lumina uses SendGrid to send:</p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Transactional emails</li>
              <li>Booking notifications</li>
              <li>Account communications</li>
              <li>System alerts</li>
            </ul>
            <p className="mt-4">
              SendGrid processes email addresses and message content solely for delivery purposes and does not use this 
              information for its own marketing.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">6. Cookies and Tracking Technologies</h2>
            <p>We may use cookies and similar technologies to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Maintain user sessions</li>
              <li>Analyze Platform usage</li>
              <li>Improve functionality and performance</li>
            </ul>
            <p className="mt-4">
              You may control cookies through your browser settings; however, disabling cookies may limit functionality.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">7. Data Retention</h2>
            <p>We retain personal information only for as long as necessary to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Provide the Platform</li>
              <li>Comply with legal obligations</li>
              <li>Resolve disputes</li>
              <li>Enforce agreements</li>
            </ul>
            <p className="mt-4">
              Content may be retained after account termination as required by law or for legitimate business purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">8. Data Security</h2>
            <p>
              We implement reasonable administrative, technical, and physical safeguards to protect personal information, including:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Secure authentication methods</li>
              <li>Encrypted connections</li>
              <li>Access controls</li>
              <li>Infrastructure security via Cloudflare</li>
            </ul>
            <p className="mt-4">
              However, no system is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">9. Your Rights and Choices</h2>
            <p>Depending on your location, you may have rights to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Access personal data</li>
              <li>Request correction or deletion</li>
              <li>Object to or restrict processing</li>
              <li>Withdraw consent where applicable</li>
            </ul>
            <p className="mt-4">
              Requests may be submitted to{" "}
              <a href="mailto:support@360lumina.com" className="text-primary hover:underline">support@360lumina.com</a>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">10. International Data Transfers</h2>
            <p>
              Your information may be processed and stored in countries other than your own, including the United States, 
              where data protection laws may differ.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">11. Children's Privacy</h2>
            <p>
              The Platform is not intended for individuals under the age of 18. We do not knowingly collect data from children.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">12. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Updates will be posted on the Platform with a revised 
              effective date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-serif mb-4">13. Contact Information</h2>
            <p>
              360Lumina LLC<br />
              Email: <a href="mailto:support@360lumina.com" className="text-primary hover:underline">support@360lumina.com</a>
            </p>
          </section>
        </div>

        {/* Related Links */}
        <div className="mt-12 pt-8 border-t border-border">
          <h3 className="text-lg font-medium mb-4">Related Policies</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
            <Link to="/dmca" className="text-primary hover:underline">DMCA Copyright Policy</Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
